import React, { useMemo, useState } from 'react';
import {
    FaBolt,
    FaCloudRain,
    FaCommentDots,
    FaFlask,
    FaHeartbeat,
    FaShieldAlt,
    FaTimes
} from 'react-icons/fa';
import PageView from '../common/pageView';
import globalStyles from '../globals.module.css';
import componentStyles from '../research/research.module.css';

const games = [
    {
        id: 'det-gb', away: 'DET', awayName: 'Lions', home: 'GB', homeName: 'Packers',
        time: 'SUN 4:25 PM', network: 'FOX', line: 'DET -2.5', total: 'O/U 49.5',
        awayRecord: '8–2', homeRecord: '6–4', awayScore: 27.8, homeScore: 24.1,
        winAway: 58, weather: '39° · 12 MPH W', venue: 'Lambeau Field', edge: 'DET PASS OFFENSE', edgeGrade: 'A-',
        summary: 'Detroit’s early-down passing efficiency meets a Green Bay secondary allowing explosive gains on 12.8% of targets.',
        injuries: [
            { team: 'DET', player: 'Taylor Decker', pos: 'LT', status: 'QUESTIONABLE', impact: 'High', note: 'Ankle · limited Thursday' },
            { team: 'GB', player: 'Jaire Alexander', pos: 'CB', status: 'QUESTIONABLE', impact: 'High', note: 'Shoulder · DNP Thursday' },
            { team: 'GB', player: 'Josh Jacobs', pos: 'RB', status: 'PROBABLE', impact: 'Med', note: 'Knee · full Thursday' }
        ],
        players: [
            { player: 'Jared Goff', pos: 'QB', team: 'DET', avg: '278.4 YDS', trend: '+8.6%', matchup: '7.8 Y/A allowed', grade: 86 },
            { player: 'Amon-Ra St. Brown', pos: 'WR', team: 'DET', avg: '82.1 YDS', trend: '+12.1%', matchup: '29% slot target rate', grade: 91 },
            { player: 'Jordan Love', pos: 'QB', team: 'GB', avg: '244.7 YDS', trend: '-3.4%', matchup: '31.2% pressure rate', grade: 74 },
            { player: 'Josh Jacobs', pos: 'RB', team: 'GB', avg: '91.3 YDS', trend: '+6.8%', matchup: '4.1 YPC allowed', grade: 79 }
        ]
    },
    {
        id: 'buf-kc', away: 'BUF', awayName: 'Bills', home: 'KC', homeName: 'Chiefs',
        time: 'SUN 8:20 PM', network: 'NBC', line: 'KC -1.5', total: 'O/U 47.0',
        awayRecord: '7–3', homeRecord: '9–1', awayScore: 28.3, homeScore: 25.6,
        winAway: 47, weather: '46° · CLEAR', venue: 'Arrowhead Stadium', edge: 'KC THIRD DOWN', edgeGrade: 'B+',
        summary: 'Two efficient quarterbacks, but Kansas City owns the stronger late-down defense and a meaningful home-field advantage.',
        injuries: [
            { team: 'BUF', player: 'Dalton Kincaid', pos: 'TE', status: 'QUESTIONABLE', impact: 'Med', note: 'Knee · limited Thursday' },
            { team: 'KC', player: 'Isiah Pacheco', pos: 'RB', status: 'OUT', impact: 'High', note: 'Fibula · inactive' }
        ],
        players: [
            { player: 'Josh Allen', pos: 'QB', team: 'BUF', avg: '267.9 YDS', trend: '+9.2%', matchup: '6.7 Y/A allowed', grade: 88 },
            { player: 'James Cook', pos: 'RB', team: 'BUF', avg: '78.6 YDS', trend: '+4.2%', matchup: '4.4 YPC allowed', grade: 81 },
            { player: 'Patrick Mahomes', pos: 'QB', team: 'KC', avg: '281.2 YDS', trend: '+2.1%', matchup: '22.1% blitz rate', grade: 89 }
        ]
    },
    {
        id: 'phi-dal', away: 'PHI', awayName: 'Eagles', home: 'DAL', homeName: 'Cowboys',
        time: 'MON 8:15 PM', network: 'ESPN', line: 'PHI -4.0', total: 'O/U 46.5',
        awayRecord: '8–2', homeRecord: '5–5', awayScore: 26.9, homeScore: 22.8,
        winAway: 64, weather: 'DOME · 72°', venue: 'AT&T Stadium', edge: 'PHI RUSH OFFENSE', edgeGrade: 'A',
        summary: 'Philadelphia’s rushing success rate creates the slate’s clearest trench advantage against an undersized Dallas front.',
        injuries: [
            { team: 'PHI', player: 'Dallas Goedert', pos: 'TE', status: 'PROBABLE', impact: 'Med', note: 'Hamstring · full Thursday' },
            { team: 'DAL', player: 'Micah Parsons', pos: 'EDGE', status: 'QUESTIONABLE', impact: 'High', note: 'Ankle · limited Thursday' }
        ],
        players: [
            { player: 'Jalen Hurts', pos: 'QB', team: 'PHI', avg: '239.8 YDS', trend: '+5.7%', matchup: '8 rush TD allowed', grade: 90 },
            { player: 'Saquon Barkley', pos: 'RB', team: 'PHI', avg: '108.4 YDS', trend: '+14.6%', matchup: '4.7 YPC allowed', grade: 95 },
            { player: 'CeeDee Lamb', pos: 'WR', team: 'DAL', avg: '88.2 YDS', trend: '-1.8%', matchup: '31% target share', grade: 84 }
        ]
    }
];

const matchupRows = [
    ['EPA / PLAY', '+0.14', '3rd', '+0.05', '12th'],
    ['SUCCESS RATE', '48.9%', '5th', '44.1%', '18th'],
    ['EXPLOSIVE RATE', '13.2%', '4th', '10.1%', '21st'],
    ['PRESSURE RATE', '28.4%', '19th', '36.7%', '5th'],
    ['RED ZONE', '67.8%', '6th', '58.1%', '17th']
];

const tabs = ['OVERVIEW', 'PLAYERS', 'HISTORY', 'INJURIES'];

const NflResearch = ({ header }) => {
    const [selectedGameId, setSelectedGameId] = useState(games[0].id);
    const [activeTab, setActiveTab] = useState('OVERVIEW');
    const [assistantOpen, setAssistantOpen] = useState(false);
    const [assistantClosing, setAssistantClosing] = useState(false);
    const [question, setQuestion] = useState('');
    const [messages, setMessages] = useState([]);
    const [pace, setPace] = useState('NEUTRAL');
    const [weather, setWeather] = useState(true);
    const [simulation, setSimulation] = useState(null);
    const game = useMemo(() => games.find((item) => item.id === selectedGameId) || games[0], [selectedGameId]);

    const selectGame = (id) => {
        setSelectedGameId(id);
        setSimulation(null);
        setActiveTab('OVERVIEW');
    };

    const runSimulation = () => {
        const paceShift = pace === 'FAST' ? 3 : pace === 'SLOW' ? -3 : 0;
        const weatherShift = weather && game.weather.includes('MPH') ? -1.5 : 0;
        setSimulation({
            away: Math.round(game.awayScore + paceShift / 2),
            home: Math.round(game.homeScore + paceShift / 2 + weatherShift),
            win: Math.max(12, Math.min(88, game.winAway + paceShift - weatherShift)),
            total: (game.awayScore + game.homeScore + paceShift + weatherShift).toFixed(1)
        });
    };

    const askResearch = (event) => {
        event.preventDefault();
        const prompt = question.trim();
        if (!prompt) return;
        setMessages((current) => [...current, { prompt, answer: `${game.away} has the cleaner efficiency profile in this scenario. The largest driver is early-down EPA, but the result is sensitive to ${game.injuries[0].player}'s availability. [Matchup table] [Injury report]` }]);
        setQuestion('');
    };

    const openAssistant = () => {
        setAssistantClosing(false);
        setAssistantOpen(true);
    };

    const closeAssistant = () => {
        if (assistantClosing) return;
        setAssistantClosing(true);
        window.setTimeout(() => {
            setAssistantOpen(false);
            setAssistantClosing(false);
        }, 240);
    };

    return (
        <PageView header={header} headerClassName={componentStyles.researchHeader} contentClassName={componentStyles.researchContent}>
            <section className={componentStyles.slate} aria-label="NFL game slate">
                <div className={componentStyles.slateHeader}><span>GAME SLATE</span><span>3 GAMES · LINES UPDATED 2M AGO</span></div>
                <div className={componentStyles.gameStrip}>
                    {games.map((item) => (
                        <button key={item.id} type="button" onClick={() => selectGame(item.id)} className={`${componentStyles.gameTile} ${item.id === game.id ? componentStyles.gameTileActive : ''}`}>
                            <span className={componentStyles.gameTime}>{item.time}</span>
                            <span className={componentStyles.gameTeams}><b>{item.away}</b><small>{item.awayRecord}</small><em>@</em><b>{item.home}</b><small>{item.homeRecord}</small></span>
                            <span className={componentStyles.gameMarket}>{item.line}<i>{item.total}</i></span>
                        </button>
                    ))}
                </div>
            </section>

            <section className={componentStyles.matchupHero}>
                <div className={componentStyles.teamBlock}><span>{game.away}</span><strong>{game.awayName}</strong><small>{game.awayRecord} · AWAY</small></div>
                <div className={componentStyles.projectionBlock}>
                    <span>{game.time} · {game.network}</span>
                    <div><b>{game.awayScore}</b><em>MODEL</em><b>{game.homeScore}</b></div>
                    <small>{game.venue} · {game.weather}</small>
                </div>
                <div className={`${componentStyles.teamBlock} ${componentStyles.teamBlockHome}`}><span>{game.home}</span><strong>{game.homeName}</strong><small>{game.homeRecord} · HOME</small></div>
                <div className={componentStyles.marketRibbon}>
                    <span>MARKET <b>{game.line}</b></span><span>TOTAL <b>{game.total.replace('O/U ', '')}</b></span><span>WIN PROB <b>{game.away} {game.winAway}%</b></span><span>TOP EDGE <b>{game.edge} {game.edgeGrade}</b></span>
                </div>
            </section>

            <div className={componentStyles.workspace}>
                <main className={componentStyles.mainPanel}>
                    <div className={componentStyles.tabs} role="tablist">
                        {tabs.map((tab) => <button type="button" role="tab" aria-selected={activeTab === tab} className={activeTab === tab ? componentStyles.tabActive : ''} onClick={() => setActiveTab(tab)} key={tab}>{tab}{tab === 'INJURIES' && <span>{game.injuries.length}</span>}</button>)}
                    </div>

                    {activeTab === 'OVERVIEW' && <>
                        <section className={componentStyles.insightPanel}>
                            <div className={componentStyles.sectionLabel}><FaBolt /> MATCHUP READ</div>
                            <p>{game.summary}</p>
                            <div className={componentStyles.signalRow}><span><b>+6.4%</b> PASS EDGE</span><span><b>+3.1</b> NET EPA</span><span><b>MED</b> VOLATILITY</span></div>
                        </section>
                        <section className={componentStyles.dataPanel}>
                            <div className={componentStyles.panelHeading}><span>TEAM MATCHUP</span><small>OFFENSE · LAST 8 GAMES</small></div>
                            <div className={componentStyles.statTable}>
                                <div className={componentStyles.tableHead}><span>METRIC</span><b>{game.away}</b><small>RK</small><b>{game.home}</b><small>RK</small></div>
                                {matchupRows.map((row) => <div className={componentStyles.tableRow} key={row[0]}><span>{row[0]}</span><b>{row[1]}</b><small>{row[2]}</small><b>{row[3]}</b><small>{row[4]}</small></div>)}
                            </div>
                        </section>
                        <section className={componentStyles.splitGrid}>
                            <div className={componentStyles.dataPanel}><div className={componentStyles.panelHeading}><span>SIMILAR OPPONENTS</span><small>3-GAME SAMPLE</small></div><div className={componentStyles.bigStat}>2–1 <small>SU</small></div><p>{game.away} averaged 6.2 yards/play against top-10 pressure defenses.</p></div>
                            <div className={componentStyles.dataPanel}><div className={componentStyles.panelHeading}><span>HISTORICAL SERIES</span><small>LAST 5</small></div><div className={componentStyles.bigStat}>3–2 <small>{game.away}</small></div><p>Average margin: +2.8 · Average total: 47.6 points.</p></div>
                        </section>
                    </>}

                    {activeTab === 'PLAYERS' && <section className={componentStyles.dataPanel}><div className={componentStyles.panelHeading}><span>PLAYER FORM + MATCHUP</span><small>SEASON AVG</small></div>{game.players.map((player) => <div className={componentStyles.playerRow} key={player.player}><div><b>{player.player}</b><small>{player.team} · {player.pos}</small></div><strong>{player.avg}</strong><span className={player.trend.startsWith('+') ? componentStyles.positive : componentStyles.negative}>{player.trend}</span><em>{player.matchup}</em><i style={{'--grade': `${player.grade}%`}}>{player.grade}</i></div>)}</section>}

                    {activeTab === 'HISTORY' && <section className={componentStyles.dataPanel}><div className={componentStyles.panelHeading}><span>SITUATIONAL HISTORY</span><small>FILTERED SPLITS</small></div>{[['VS TOP-10 DEFENSES','4–2','+5.6 margin'],['AS ROAD FAVORITE','6–3','66.7% cover'],['DIVISION GAMES','7–3','24.8 PPG'],['7 DAYS REST','8–2','+0.11 EPA/play']].map((row) => <div className={componentStyles.historyRow} key={row[0]}><span>{row[0]}</span><b>{row[1]}</b><small>{row[2]}</small></div>)}</section>}

                    {activeTab === 'INJURIES' && <section className={componentStyles.dataPanel}><div className={componentStyles.panelHeading}><span>INJURY IMPACT</span><small>PROJECTED STATUS</small></div>{game.injuries.map((injury) => <div className={componentStyles.injuryRow} key={injury.player}><span>{injury.team}</span><div><b>{injury.player}</b><small>{injury.pos} · {injury.note}</small></div><strong>{injury.status}</strong><em>{injury.impact} impact</em></div>)}</section>}
                </main>

                <aside className={componentStyles.sidePanel}>
                    <section className={componentStyles.simulator}>
                        <div className={componentStyles.sectionLabel}><FaFlask /> SCENARIO LAB</div>
                        <p>Adjust assumptions, then generate a mock outcome distribution.</p>
                        <label>EXPECTED PACE<select className={globalStyles.standardSelect} value={pace} onChange={(event) => setPace(event.target.value)}><option>SLOW</option><option>NEUTRAL</option><option>FAST</option></select></label>
                        <label className={componentStyles.toggleRow}><input type="checkbox" className={globalStyles.standardCheckbox} checked={weather} onChange={(event) => setWeather(event.target.checked)} /> APPLY WEATHER</label>
                        <button type="button" className={globalStyles.primaryButton} onClick={runSimulation}>RUN 10K SIMS</button>
                        {simulation && <div className={componentStyles.simResult}><small>MEDIAN SCORE</small><strong>{game.away} {simulation.away} — {simulation.home} {game.home}</strong><div><span>{game.away} WIN <b>{simulation.win}%</b></span><span>AVG TOTAL <b>{simulation.total}</b></span></div></div>}
                    </section>
                    <section className={componentStyles.watchPanel}>
                        <div className={componentStyles.sectionLabel}><FaHeartbeat /> WATCH LIST</div>
                        {game.injuries.slice(0, 2).map((injury) => <div key={injury.player}><FaShieldAlt /><span><b>{injury.player}</b><small>{injury.status} · {injury.note}</small></span></div>)}
                        <div><FaCloudRain /><span><b>Weather sensitivity</b><small>Wind above 15 MPH lowers total 2.1 pts</small></span></div>
                    </section>
                </aside>
            </div>

            <button type="button" className={componentStyles.aiLauncher} onClick={openAssistant}><FaCommentDots /><span>ASK THE DATA</span></button>
            {assistantOpen && <div className={`${componentStyles.aiPanel} ${assistantClosing ? componentStyles.aiPanelClosing : componentStyles.aiPanelOpening}`}>
                <div className={componentStyles.aiHeader}><div><FaCommentDots /><span>RESEARCH AI<small>GROUNDED IN THIS MATCHUP</small></span></div><button type="button" onClick={closeAssistant} aria-label="Close research AI"><FaTimes /></button></div>
                <div className={componentStyles.aiBody}>
                    <p className={componentStyles.aiIntro}>Ask about matchup edges, injuries, player usage, or scenario sensitivity.</p>
                    {messages.map((message, index) => <div key={`${message.prompt}-${index}`}><div className={componentStyles.userMessage}>{message.prompt}</div><div className={componentStyles.aiMessage}>{message.answer}</div></div>)}
                </div>
                <form className={componentStyles.aiForm} onSubmit={askResearch}><input className={globalStyles.standardInput} value={question} onChange={(event) => setQuestion(event.target.value)} placeholder={`Ask about ${game.away} @ ${game.home}…`} /><button className={globalStyles.primaryButton} type="submit">ASK</button></form>
            </div>}
        </PageView>
    );
};

export default NflResearch;
